/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useContext } from "react";
import { RosterData } from "RosterData";
import { DateTime } from "luxon";
import { AuthenticatedContext } from "../Authenticated/Authenticated";
import { Typography } from "@mui/material";
import PersonnelCell from "../Cells/PersonnelCell";
import MovementCell from "../Cells/MovementCell";
import "./index.css";
import Options from "../Options/Options";
import { MoodBad } from "@mui/icons-material";
import DateColumn from "./DateColumn";
import VirtualTable from "./VirtualTable";

export interface Column {
  key: string;
  name: any;
  width: number;
}

const RosterPanelTable = ({ loading }: { loading: boolean }) => {
  const { displayController, interval, reportData, totals, trafficData } =
    useContext(AuthenticatedContext);

  const getPersonnelColumnWidth = (): number => {
    let baseWidth = 210;
    if (displayController!.showPersonnelId) baseWidth += 100;
    if (displayController!.showGender) baseWidth += 45;
    if (displayController!.showEmployer) baseWidth += 100;
    if (displayController!.showDiscipline) baseWidth += 100;
    if (displayController!.showJobTitle) baseWidth += 100;
    if (displayController!.showCoreCrew) baseWidth += 73;
    if (displayController!.showTeam) baseWidth += 100;
    if (displayController!.showDeptCode) baseWidth += 100;
    return baseWidth > 520 ? 520 : baseWidth;
  };

  const getDateColumnWidth = (): number => {
    let baseWidth = 75;
    if (displayController!.showTraffic) baseWidth += 35;
    // if (displayController!.showTotals) baseWidth += 45;
    return baseWidth > 125 ? 125 : baseWidth;
  };

  const generateColumns = () => {
    let arr1 = [
      {
        key: "0",
        name: <Options width={getPersonnelColumnWidth()} />,
        width: getPersonnelColumnWidth(),
      },
    ];
    const arr2 = interval!.map((date: DateTime, i) => {
      const key: string = i.toString();
      return {
        key: key + 1,
        name: (
          <DateColumn
            date={date}
            showTotals={displayController!.showTotals}
            total={totals ? totals[i] : null}
            traffic={trafficData ? trafficData[i] : null}
          />
        ),
        width: getDateColumnWidth(),
      };
    });
    return arr1.concat(arr2);
  };

  const generateData = (columns: Column[], count = 300) => {
    const arrNew = reportData!.map((reportRecord: RosterData, rowIndex) => {
      let colSpan = 1;
      let movementIndex = 0;
      return columns.reduce<any[]>((acc, _curr, colIndex) => {
        let val;
        if (colIndex === 0) {
          val = (
            <PersonnelCell
              personnelData={reportRecord.data}
              showPersonnelId={displayController!.showPersonnelId}
            />
          );
        } else {
          val = (
            <MovementCell
              cellType={reportRecord.movements[movementIndex].cellType}
              location={reportRecord.movements[movementIndex].location}
              flightName={reportRecord.movements[movementIndex].flightName}
              departureLocation={
                reportRecord.movements[movementIndex].departureLocation
              }
              arrivalLocation={
                reportRecord.movements[movementIndex].arrivalLocation
              }
              fromTo={reportRecord.movements[movementIndex].fromTo}
              multiple={reportRecord.movements[movementIndex].colSpan > 1}
              start={colSpan === 1}
              end={colSpan === reportRecord.movements[movementIndex].colSpan}
              layer1={reportRecord.movements[movementIndex].layer1}
            />
          );
          if (colSpan < reportRecord.movements[movementIndex].colSpan) {
            colSpan++;
          } else {
            colSpan = 1;
            movementIndex++;
          }
        }
        acc.push(val);
        return acc;
      }, []);
    });
    if (arrNew.length === 0) {
      arrNew.push(
        columns.reduce<any[]>((acc, _curr, colIndex) => {
          let val;
          if (colIndex === 0) {
            val = (
              <div
                style={{
                  display: "flex",
                }}
              >
                <Typography mr={1}>No Results</Typography>
                <MoodBad />
              </div>
            );
          } else {
            val = <></>;
          }
          acc.push(val);
          return acc;
        }, [])
      );
    }
    arrNew.unshift([]);
    return arrNew;
  };

  const generatedColumns = generateColumns();
  const data = generateData(generatedColumns);

  return (
    <>
      {loading ? (
        <></>
      ) : (
        <VirtualTable
          columns={generatedColumns}
          data={data}
          getPersonnelColumnWidth={getPersonnelColumnWidth}
        />
      )}
    </>
  );
};

export default React.memo(RosterPanelTable);
